import { DialogAccessCredentials, ModalParts } from 'BreetComponents';
import { handleApiError, type IUseAccessCredentialsRequest, OTPLENGTH, showToast } from 'BreetHelpers';
import { useAccessCredentials, useFormStage, useMfaApi, useModalApi } from 'BreetHooks';
import {
	useActivate2faAuthenticatorMutation,
	useDeActivate2faAuthenticatorMutation,
	useDisable2faEmailMutation,
	useReActivate2faAuthenticatorMutation,
} from 'BreetRedux';
import { useCallback, useEffect, useRef } from 'react';

import AuthenticatorBackupCode from './components/AuthenticatorBackupCode';
import AuthenticatorQr from './components/AuthenticatorQr';
import { type TwoFaActionType, type TwoFaCategory, twoFaStageTitle, type TWOFASTAGETYPE } from './Dialog2faSettings.helpers';

const Dialog2faSettings = () => {
	const { getModalProps, closeModalDirect, handleModalCloseAll } = useModalApi();

	const modalProps = getModalProps('twoFaSettings');

	const { category, actionType } = modalProps?.metadata as { category: TwoFaCategory; actionType: TwoFaActionType };

	const { handleEmail2faActivation } = useMfaApi();

	const [disable2faEmail] = useDisable2faEmailMutation();
	const [activate2faAuthenticator, { data: activate2faAuthenticatorData }] = useActivate2faAuthenticatorMutation();
	const [reActivate2faAuthenticator] = useReActivate2faAuthenticatorMutation();
	const [deActivate2faAuthenticator] = useDeActivate2faAuthenticatorMutation();

	const initStageObject: Record<TwoFaActionType, TWOFASTAGETYPE> = {
		'enable-email': 'email-otp',
		'disable-email': 'email-otp',
		'init-authenticator': 'authenticator-qr',
		'activate-authenticator': 'authenticator-otp',
		'deactivate-authenticator': 'authenticator-otp',
	};

	const initStage = initStageObject[actionType];

	const formStage = useFormStage<TWOFASTAGETYPE>(
		initStage,
		['email-otp', 'authenticator-qr', 'authenticator-otp', 'authenticator-backup-code'],
		twoFaStageTitle
	);

	const descriptionObj: Record<TwoFaActionType, string> = {
		'enable-email': `Enter the ${OTPLENGTH}-digit code sent to your <span class='color-tertiary-400'>email inbox</span> to continue.`,
		'disable-email': `Enter the ${OTPLENGTH}-digit code sent to your <span class='color-tertiary-400'>email inbox</span> to continue.`,
		'init-authenticator': `Enter the ${OTPLENGTH}-digit code from your <span class='color-tertiary-400'>authenticator app</span> to continue.`,
		'activate-authenticator': `Enter the ${OTPLENGTH}-digit code from your <span class='color-tertiary-400'>authenticator app</span> to continue.`,
		'deactivate-authenticator': `Enter the ${OTPLENGTH}-digit code from your <span class='color-tertiary-400'>authenticator app</span> to continue.`,
	};

	const description = descriptionObj[actionType];

	const fetchOtpApi = useCallback(() => {
		// apis that need to be hit to get otp
		let apiRequest;

		if (actionType === 'disable-email') {
			apiRequest = disable2faEmail;
		}

		if (!apiRequest) return;

		return apiRequest().unwrap();
	}, [actionType, disable2faEmail]);

	const handle2faEmailOtp = useCallback(
		(data?: IUseAccessCredentialsRequest) => {
			if (!data?.code) {
				return;
			}

			let apiRequest = disable2faEmail;

			if (actionType === 'deactivate-authenticator') {
				apiRequest = deActivate2faAuthenticator;
			}

			apiRequest({ otp: data.code })
				.unwrap()
				.then(() => {
					data.onSuccess?.();

					if (actionType === 'disable-email') {
						showToast('success', 'Email 2FA has been disabled');
					}

					if (actionType === 'deactivate-authenticator') {
						handleEmail2faActivation();
					}

					handleModalCloseAll();
				})
				.catch((e: unknown) => {
					closeModalDirect('loadingDialog');
					showToast('error', 'Failed to update 2FA settings');
					console.error(e);
				});
		},
		[disable2faEmail, actionType, deActivate2faAuthenticator, handleEmail2faActivation, handleModalCloseAll, closeModalDirect]
	);

	const handle2faAuthenticatorOtp = useCallback(
		(data?: IUseAccessCredentialsRequest) => {
			if (!data?.code) {
				showToast('error', 'Please enter a valid OTP');
				return;
			}

			let apiRequest;

			if (actionType === 'init-authenticator') {
				apiRequest = activate2faAuthenticator;
			}

			if (actionType === 'activate-authenticator') {
				apiRequest = reActivate2faAuthenticator;
			}

			if (actionType === 'deactivate-authenticator') {
				apiRequest = deActivate2faAuthenticator;
			}

			if (!apiRequest) {
				return;
			}

			apiRequest({ otp: data.code })
				.unwrap()
				.then(() => {
					data.onSuccess?.();

					if (actionType === 'init-authenticator') {
						closeModalDirect('loadingDialog');
						formStage.navigator.goTo('authenticator-backup-code')();
					} else {
						handleModalCloseAll();
					}
				})
				.catch((e: unknown) => {
					data.onError(e);
				});
		},
		[
			actionType,
			activate2faAuthenticator,
			reActivate2faAuthenticator,
			deActivate2faAuthenticator,
			closeModalDirect,
			formStage.navigator,
			handleModalCloseAll,
		]
	);

	const refetchOtpApi = useCallback(async () => {
		await fetchOtpApi()?.catch(handleApiError);
	}, [fetchOtpApi]);

	const { component: twoFaOtpComponent } = useAccessCredentials({
		id: 'withdraw-request',
		mode: 'otp',
		submitRequest: category !== 'email' ? handle2faAuthenticatorOtp : handle2faEmailOtp,
		dialogMessage: 'Processing request...',
		type: 'collect',
		title: 'Just one last step',
		description,
		generateOTP: false,
		resendOtp: category === 'email',
		buttonTitle: 'Submit',
		render: (props) => (
			<DialogAccessCredentials
				{...props}
				refetchFn={refetchOtpApi}
			/>
		),
	});

	const callOnceRef = useRef<boolean>(false);

	useEffect(() => {
		if (actionType === 'init-authenticator') {
			return;
		}

		if (actionType === 'disable-email' && !callOnceRef.current) {
			callOnceRef.current = true;
			refetchOtpApi().catch(console.error);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<ModalParts.Wrapper
			title={formStage.stageTitle}
			rightSheet
			root={twoFaStageTitle[initStage]}
			handleBackLink={formStage.navigator.historyBack}
		>
			<ModalParts.Stage
				hide={!formStage.isStageActive('email-otp')}
				className='two-fa-form'
			>
				{twoFaOtpComponent}
			</ModalParts.Stage>

			<ModalParts.Stage hide={!formStage.isStageActive('authenticator-qr')}>
				<AuthenticatorQr
					onNext={formStage.navigator.goTo('authenticator-otp')}
					closeAllModal={handleModalCloseAll}
				/>
			</ModalParts.Stage>

			<ModalParts.Stage
				hide={!formStage.isStageActive('authenticator-otp')}
				className='two-fa-form authenticator'
			>
				{twoFaOtpComponent}
			</ModalParts.Stage>

			<ModalParts.Stage hide={!formStage.isStageActive('authenticator-backup-code')}>
				<AuthenticatorBackupCode
					recoveryCodes={activate2faAuthenticatorData?.data?.recoveryCodes ?? []}
					onNext={handleModalCloseAll}
				/>
			</ModalParts.Stage>
		</ModalParts.Wrapper>
	);
};

export default Dialog2faSettings;
